import { Box, Stack, Tab, Tabs, alpha } from '@mui/material'

import NewBadge from '@/src/components/common/home/flightSearch/searchForm/NewBadge'
import HolidaysSpotlightWrapper from '@/src/components/common/holidays/holidaysSpotlight'
import { HighlightRing, badgeRevealSx } from '@/src/components/common/ui/highlightRing'

export interface LobTabItem {
    key: string
    label: string
    /** Localised flag beside the label, e.g. "NEW" on a line of business just launched. */
    badge?: string
    /** Rings the tab in brand colours while it is unselected, to point at a new line of business. */
    highlight?: boolean
}

export interface LobTabsProps {
    tabs: LobTabItem[]
    /** Index of the active line of business. */
    activeIndex: number
    onTabChange: (index: number) => void
    ariaLabel?: string
}

// Line-of-business capsules shared by the flights and hotels home search forms
// (Flights | Hotels | Flights + Hotels / Visa). Pill row that overlaps the form card
// below it. Desktop-only, matching the home layout on both verticals.
const LobTabs: React.FC<LobTabsProps> = ({ tabs, activeIndex, onTabChange, ariaLabel = 'line of business tabs' }) => (
    <Box
        sx={{
            display: { xs: 'none', md: 'flex' },
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: 'common.white',
            borderRadius: 5,
            border: 1,
            borderColor: 'grey.200',
            py: 1.5,
            px: 2.5,
            width: 'fit-content',
            mx: 'auto',
            position: 'relative',
            zIndex: 2
        }}
    >
        <Tabs
            value={activeIndex}
            onChange={(_, next: number) => onTabChange(next)}
            aria-label={ariaLabel}
            sx={{
                minHeight: 'unset',
                // Tabs, its scroller and each Tab all clip their content by default, which cuts
                // off anything drawn outside a tab's own box — the ring at the container's edge,
                // and the spotlight bubble entirely, since it sits above the row.
                overflow: 'visible',
                '& .MuiTabs-scroller': { overflow: 'visible !important' },
                '& .MuiTab-root.MuiTab-root': { overflow: 'visible' },
                '& .MuiTabs-flexContainer': { gap: 1 },
                '& .MuiTab-root': {
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: 'grey.600',
                    px: 3,
                    py: 1,
                    minWidth: 'unset',
                    minHeight: 'unset',
                    borderRadius: 0.5,
                    position: 'relative',
                    transition: 'all 0.2s ease',
                    '&.Mui-selected': {
                        fontWeight: 600,
                        color: 'primary.main',
                        bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                        // Pill outline via an inset shadow rather than `border` (no layout shift).
                        boxShadow: theme => `inset 0 0 0 1.5px ${alpha(theme.palette.primary.main, 0.3)}`,
                        borderRadius: 5,
                        zIndex: 1
                    }
                },
                '& .MuiTabs-indicator': { display: 'none' }
            }}
        >
            {tabs.map((tab, index) => {
                /*
                 * The ring and the spotlight exist to point at a tab the reader has not tried.
                 * Once it is the selected tab — or the page they are already on — both have made
                 * their point, and leaving them up covers the page's own copy.
                 */
                const isHighlighted = Boolean(tab.highlight) && index !== activeIndex

                return (
                    <Tab
                        key={tab.key}
                    // A highlighted tab wears a lighter version of the selected pill, so it reads
                    // as the one worth trying without pretending to be the current tab.
                    sx={
                        tab.highlight
                            ? {
                                  '&:not(.Mui-selected)': {
                                      color: 'secondary.main',
                                      fontWeight: 600,
                                      borderRadius: 5
                                  }
                              }
                            : undefined
                    }
                    label={
                        tab.badge || tab.highlight ? (
                            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                                <span>{tab.label}</span>
                                {tab.badge && (
                                    <Box component="span" sx={badgeRevealSx}>
                                        <NewBadge label={tab.badge} />
                                    </Box>
                                )}
                                {isHighlighted && (
                                    <>
                                        <HighlightRing />
                                        <HolidaysSpotlightWrapper caret="down" />
                                    </>
                                )}
                            </Stack>
                        ) : (
                            tab.label
                        )
                    }
                    />
                )
            })}
        </Tabs>
    </Box>
)

export { LobTabs }
export default LobTabs
